import React, { useState, useEffect } from 'react';
import { Box, Container, TextField, Button, Typography, Paper, Alert, Select, MenuItem, InputLabel, FormControl, Autocomplete } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../../services/api';
import apiEndPointsConfig from '../../../services/apiEndPoints';


const AgentCreateForm = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    companyName: '',
    companyId: '',
    firstName: '',
    lastName: '',
    userName: '',
    dialerUserName: '',
    agentId: '',
    mobile: '',
    email: '',
    password: '',
    role: 'Agent',
    gender: '',
    processType: '',
    analysisType: 'both',
    status: 'active',
  });
  const [companies, setCompanies] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const token = localStorage.getItem("token");

        const response = await axiosInstance.get(apiEndPointsConfig.companyList, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const list = response?.data?.data || {};
        const formatted = Object.entries(list).map(([name, id]) => ({
          name,
          id,
        }));
        setCompanies(formatted);
      } catch (err) {
        console.error("Failed to fetch companies:", err);
      }
    };
    fetchCompanies();
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      const response = await axiosInstance.post('/super-admin-panel/agents', form);
      setSuccess(response.data?.message || 'Agent created successfully');
      setTimeout(() => navigate('/super-admin/dashboard'), 1500);
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to create agent';
      setError(msg);
    }
  };

  return (
    <Box sx={{ background: "linear-gradient(135deg, #e0e0ff, #f8f9fa)", minHeight: "80vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
    <Container maxWidth="md" sx={{ p: { xs: 3, md: 5 }, borderRadius: 4, background: "rgba(255,255,255,0.6)", backdropFilter: "blur(12px)", boxShadow: "0 8px 24px rgba(0,0,0,0.1)", border: "1px solid rgba(255,255,255,0.2)", maxWidth: "1600px", width: "100%" }}>
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h5" fontWeight="bold" color="#2c3e50" gutterBottom>
          Create New User
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Provide the details below to register a new user in the platform.
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>{success}</Alert>}

      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3, mb: 4 }}>
          <Autocomplete
            options={companies}
            getOptionLabel={(option) => option.name || ""}
            value={companies.find(c => c.name === form.companyName) || null}
            onChange={(event, newValue) => {
              setForm(prev => ({
                ...prev,
                companyName: newValue ? newValue.name : '',
                companyId: newValue ? newValue.id : ''
              }));
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Company Name"
                required
                size="small"
                name="companyName"
              />
            )}
          />
          <TextField
            name="firstName"
            label="First Name"
            value={form.firstName}
            onChange={handleChange}
            required
            fullWidth
            size="small"
          />
          <TextField
            name="lastName"
            label="Last Name"
            value={form.lastName}
            onChange={handleChange}
            fullWidth
            size="small"
          />
          <TextField
            name="userName"
            label="Username"
            value={form.userName}
            onChange={handleChange}
            required
            fullWidth
            size="small"
          />
          <TextField
            name="dialerUserName"
            label="Dialer Username"
            value={form.dialerUserName}
            onChange={handleChange}
            fullWidth
            size="small"
          />
          <TextField
            name="agentId"
            label="Agent ID"
            value={form.agentId}
            onChange={handleChange}
            fullWidth
            size="small"
          />
          <TextField
            name="mobile"
            label="Mobile"
            value={form.mobile}
            onChange={handleChange}
            fullWidth
            size="small"
          />
          <TextField
            name="email"
            label="Email"
            type="email"
            value={form.email}
            onChange={handleChange}
            required
            fullWidth
            size="small"
          />
          <TextField
            name="password"
            label="Password"
            type="password"
            value={form.password}
            onChange={handleChange}
            required
            fullWidth
            size="small"
          />
          <FormControl fullWidth size="small">
            <InputLabel id="role-label">Role</InputLabel>
            <Select labelId="role-label" name="role" value={form.role} label="Role" onChange={handleChange}>
              <MenuItem value="Agent">Agent</MenuItem>
              <MenuItem value="Manager">Manager</MenuItem>
              <MenuItem value="Admin">Admin</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel id="gender-label">Gender</InputLabel>
            <Select labelId="gender-label" name="gender" value={form.gender} label="Gender" onChange={handleChange}>
              <MenuItem value="male">Male</MenuItem>
              <MenuItem value="female">Female</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>
          <TextField
            name="processType"
            label="Process Type"
            value={form.processType}
            onChange={handleChange}
            fullWidth
            size="small"
          />
          <FormControl fullWidth size="small" sx={{ gridColumn: { sm: 'span 2' } }}>
            <InputLabel id="analysis-label">Analysis Type</InputLabel>
            <Select labelId="analysis-label" name="analysisType" value={form.analysisType} label="Analysis Type" onChange={handleChange}>
              <MenuItem value="postAnalysis">Post Analysis</MenuItem>
              <MenuItem value="realTimeAnalysis">Real‑Time Analysis</MenuItem>
              <MenuItem value="both">Both</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/super-admin/dashboard')}
            sx={{
              textTransform: 'none',
              borderRadius: '8px',
              px: 4,
              color: '#6c757d',
              borderColor: '#dee2e6',
              '&:hover': { borderColor: '#ced4da', backgroundColor: '#f8f9fa' }
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            sx={{
              textTransform: 'none',
              borderRadius: '8px',
              px: 4,
              backgroundColor: "#7635dc",
              '&:hover': { backgroundColor: "#5b2da7" }
            }}
          >
            Create Agent
          </Button>
        </Box>
      </Box>
    </Container>
  </Box>
  );
};

export default AgentCreateForm;
